import { Sparkles, Sprout } from "lucide-react";
import type { Event, JuniorConfig } from "@/lib/event-schema";
import { Ornament } from "@/components/ui/Ornament";

type Props = {
  config: JuniorConfig;
  event: Event;
};

export function JuniorModule({ config, event }: Props) {
  return (
    <section
      id="junior"
      className="relative mx-auto w-full max-w-5xl px-5 py-16 sm:px-6 lg:px-8 lg:py-24"
      aria-labelledby={`${event.slug}-junior`}
    >
      <header className="text-center mb-12">
        <p className="eyebrow text-accent inline-flex items-center gap-2">
          <Sprout className="h-3.5 w-3.5" strokeWidth={1.75} />
          Junior program
        </p>
        <h2
          id={`${event.slug}-junior`}
          className="display-title mt-3 text-[clamp(2rem,4.4vw,3.25rem)] leading-tight"
        >
          {config.title}
        </h2>
        {config.description ? (
          <p className="mx-auto mt-3 max-w-2xl text-balance text-muted">{config.description}</p>
        ) : null}
      </header>

      {config.ageGroups.length ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {config.ageGroups.map((group, i) => (
            <article
              key={i}
              className="surface-card group relative overflow-hidden p-6 transition-all hover:border-accent/40"
            >
              <div
                aria-hidden
                className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-accent/10 blur-2xl transition-transform group-hover:scale-125"
              />
              <p className="eyebrow text-accent">Ages</p>
              <h3 className="mt-1 font-display text-2xl font-light text-ink">
                {group.label}
              </h3>
              {group.description ? (
                <p className="mt-2 text-sm text-muted leading-relaxed">
                  {group.description}
                </p>
              ) : null}
            </article>
          ))}
        </div>
      ) : null}

      {config.stations?.length ? (
        <>
          <Ornament className="my-12" label="Stations" />
          <div className="grid gap-5 sm:grid-cols-2">
            {config.stations.map((station, i) => (
              <div
                key={i}
                className="flex items-start gap-4 rounded-2xl border border-line/60 bg-surface-raised/60 p-5 backdrop-blur"
              >
                <Sparkles className="mt-1 h-4 w-4 shrink-0 text-accent" strokeWidth={1.75} />
                <div>
                  <h4 className="font-medium text-ink">{station.name}</h4>
                  {station.description ? (
                    <p className="mt-1 text-sm text-muted leading-relaxed">{station.description}</p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}
